import { UseFormReturn } from "react-hook-form";
import { TextInput, TextInputProps } from "react-native";
import * as Yup from "yup";
import Location from "../data/model/Location";
import StockCountLineModel from "../data/model/StockCountLine";
import { FulltextSearchContextType } from "../fulltext-search/context";
import { UseSortByResult } from "../use-sort";
import { StockSelectionType, stockAdjusterValidationSchema } from "./constants";
import { StockCountTabs } from "../../modules/stock-counts/screens/StockCountsScreen/constants";
import { StockCount as StockCountType } from "@towersystems/roam-common/lib/generated-types";
import StockCount from "../data/model/StockCount";

export enum CompletionStatus {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
}

export type ScanMode = "quick" | "slow";

export type UseDownloadStockCountResponse = {
  loading: boolean;
  downloadStockCount: (stockCountId: string) => Promise<void>;
};

export type StockCountContextValue = {
  locations: Location[];
  defaultLocationId: string;
  handleExportStockCount: (stockCountId: string) => Promise<void>;
  handleExportStockCountItems: (stocktakeCountIds: string[]) => Promise<void>;
};

export type HistoryOptions = {
  productId?: string;
  hideCounted: boolean;
  hideExported: boolean;
};

export type StockCountHistoryContextType = {
  stockCountId?: string;
  stockCountLineId?: string;
  historyOptions: HistoryOptions;
  updateHistoryOptions: (historyOptions: Partial<HistoryOptions>) => void;
  updateProductIdByStockCountLineId: (stockCountLineId?: string) => void;
};

export type StockAdjusterFormContextType = {};

export type StockCountSyncContextType = {
  syncRemoteStockCount: (stockCount: StockCountType) => Promise<void>;
  syncRemoteStockCountLines: (stockCountId: string) => Promise<void>;
  loadingStockCountLines: boolean;
};

export interface StockCountItemSyncContextType {
  running: boolean;
  addCountToQueue(stockCountItemId: string): Promise<boolean>;
  processAndSyncCountById(stockCountItemId: string): Promise<void>;
  removeCountFromQueue(stockCountItemId: string): Promise<void>;
}

export interface CurrentCountContextType {
  stockCount: StockCount;
  stockSelection?: StockSelection;
  previousStockSelection?: StockSelection;
  selectedStockCountLine?: StockCountLineModel;
  stockAdjuster: UseStockAdjusterReturnType;
  resetSelections: () => void;
  updateStockSelection: (
    selectionOptions: SelectionOptions,
    callback?: () => void
  ) => Promise<void>;
  updateSelectedStockCountLineByStockIdentifier: (
    stockIdentifier: StockIdentifier,
    triggerUpdatedAt?: boolean
  ) => Promise<void>;
  updateSelectedStockCountLine: (
    stockCountLine: StockCountLineModel
  ) => Promise<void>;
}

export type SelectionOptions = StockSelection & {
  triggerUpdatedAt: boolean;
  updateFormOnly?: boolean;
};

export type StockIdentifier = {
  productId?: string;
  barcode?: string;
};

export type StockSelection = {
  stockIdentifier: StockIdentifier;
  selectionType: StockSelectionType;
};

export type StockCountSearchContextType = {
  activeTab: number;
  fullTextSearch: FulltextSearchContextType;
  scanMode: ScanMode;
  showProductView: boolean;
  sortBy: UseSortByResult<{
    counted?: number;
    expected?: number;
  }>;
  loadScanMode: () => Promise<ScanMode>;
  setActiveTab: (activeTab: number) => void;
  setShowProductView: (showProductView: boolean) => void;
  updateScanMode: (scanMode: ScanMode) => void;
};

export type StockCountTabContextType = {
  index: number;
  selectedKey: StockCountTabs;
};

export type StockAdjusterFormValues = Yup.InferType<
  typeof stockAdjusterValidationSchema
>;

export type SubmitAdjustBy = {
  stockIdentifier?: StockIdentifier;
  adjustBy: number;
};

export type UseStockAdjusterReturnType = {
  countInputRef: React.RefObject<TextInput>;
  searchInputRef: React.RefObject<TextInput>;
  methods: UseFormReturn<StockAdjusterFormValues>;
  isSubmitDisabled: boolean;
  searchFocused: boolean;
  selection?: TextInputProps["selection"];
  setSearchFocused: (searchFocused: boolean) => void;
  updateForm: (
    newStockSelection: StockSelection,
    prevStockSelection?: StockSelection
  ) => void;
  submitAdjustBy: ({
    stockIdentifier,
    adjustBy,
  }: SubmitAdjustBy) => Promise<void>;
  increaseAdjustBy: () => void;
  handleChangeAdjustBy: (value: string) => void;
  handleOnSubmitSearch: () => void;
  handleOnSubmitCount: () => void;
};

export type StockCountHistoryFormValues = {
  productId?: string;
  hideCounted: boolean;
  hideExported: boolean;
};
